import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import Hero from './Hero';
import PromoWheel from './PromoWheel';
import Services from './Services';
import PacksSection from './PacksSection';
import AISection from './AISection';
import About from './About';
import Footer from './Footer';
import ProjectSubmission from './ProjectSubmission';
import AppointmentBooking from './AppointmentBooking';
import UserMenu from './UserMenu';
import MyDiscussions from './MyDiscussions';
import { useAuth } from './AuthProvider';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showProjectSubmission, setShowProjectSubmission] = useState(false);
  const [showAppointmentBooking, setShowAppointmentBooking] = useState(false);
  const [showDiscussions, setShowDiscussions] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  
  // Utiliser le contexte d'authentification
  const { isAuthenticated, user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    // Optionnel: recharger la page pour nettoyer l'état
    window.location.reload();
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  if (showProjectSubmission) {
    return <ProjectSubmission onBack={() => setShowProjectSubmission(false)} />;
  }

  if (showAppointmentBooking) {
    return <AppointmentBooking onBack={() => setShowAppointmentBooking(false)} />;
  }

  if (showDiscussions) {
    return <MyDiscussions onBack={() => setShowDiscussions(false)} />;
  }

  return (
    <div className="App min-h-screen bg-ice text-gray-900 relative">
      {/* Logo Iris Softech à gauche */}
      <div className="fixed top-6 left-6 z-40">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-elegant text-white-gold">
          Iris Softech
        </h1>
      </div>

      {/* Menu utilisateur et boutons de navigation - Design premium gold */}
      <div className="fixed top-6 right-6 z-40 flex items-center gap-1">
        {/* Boutons de navigation - Gold élégant */}
        
        {/* Menu déroulant Services/Packs/Devis */}
        <div className="relative"
          onMouseEnter={() => setShowServicesDropdown(true)}
          onMouseLeave={() => setShowServicesDropdown(false)}
        >
          <button
            className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300 flex items-center gap-1"
          >
            Nos offres
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showServicesDropdown && (
            <div className="absolute top-full right-0 mt-1 bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg shadow-2xl border border-amber-500/20 min-w-[160px] overflow-hidden">
              <button
                onClick={() => navigate('/services')}
                className="w-full px-4 py-2.5 text-left text-amber-200 hover:text-amber-100 hover:bg-amber-500/10 text-sm font-light tracking-wide transition-all duration-200"
              >
                Services
              </button>
              <button
                onClick={() => navigate('/packs')}
                className="w-full px-4 py-2.5 text-left text-amber-200 hover:text-amber-100 hover:bg-amber-500/10 text-sm font-light tracking-wide transition-all duration-200"
              >
                Packs
              </button>
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    navigate('/devisrequest');
                  } else {
                    localStorage.setItem('redirectAfterLogin', '/devisrequest');
                    navigate('/login');
                  }
                }}
                className="w-full px-4 py-2.5 text-left text-amber-200 hover:text-amber-100 hover:bg-amber-500/10 text-sm font-light tracking-wide transition-all duration-200"
              >
                Devis
              </button>
            </div>
          )}
        </div>
        
        <button
          onClick={() => setShowAppointmentBooking(true)}
          className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
        >
          Rendez-vous
        </button>
        
        <button
          onClick={() => navigate('/promowheel')}
          className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
        >
          Roue Promo
        </button>
        
        <button
          onClick={() => {
            if (isAuthenticated) {
              navigate('/projets');
            } else {
              localStorage.setItem('redirectAfterLogin', '/projets');
              navigate('/login');
            }
          }}
          className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
        >
          Projets
        </button>
        
        <button
          onClick={() => navigate('/aboutirissoftech')}
          className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
        >
          À propos
        </button>
        
        <button
          onClick={() => navigate('/contact')}
          className="px-3 py-1.5 text-amber-200 hover:text-amber-100 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
        >
          Contactez-nous
        </button>
        
        {/* Séparateur subtil gold */}
        {!isAuthenticated && (
          <div className="w-px h-4 bg-amber-400/30 mx-1"></div>
        )}
        
        {/* Bouton Se connecter - Gold premium */}
        {!isAuthenticated && (
          <button
            onClick={handleLoginClick}
            className="px-3 py-1.5 text-amber-100 hover:text-amber-50 text-sm font-light tracking-wide hover:bg-amber-500/10 rounded transition-all duration-300"
          >
            Se connecter
          </button>
        )}
        
        {/* Menu utilisateur */}
        {isAuthenticated && user && (
          <>
            <div className="w-px h-4 bg-amber-400/30 mx-1"></div>
            <UserMenu 
              user={user} 
              onLogout={handleLogout}
              onShowDiscussions={() => setShowDiscussions(true)}
              onShowDashboardAdmin={() => navigate('/admin')}
            />
          </>
        )}
      </div>

      <Hero 
        onAdminClick={() => navigate('/admin')}
        onProjectSubmit={() => setShowProjectSubmission(true)}
        onAppointmentBook={() => setShowAppointmentBooking(true)}
        isAuthenticated={isAuthenticated}
        onLoginClick={handleLoginClick}
      />
      <PromoWheel />
      <Services />
      <PacksSection />
      <AISection />
      <About />
      <Footer />
    </div>
  );
};

export default HomePage;
